/* threadkins custom — ADD A VASE sheet behaviour (Phase 2 V1, 2026-07-27;
 * hardened 2026-07-28; select-then-commit model 2026-07-28).
 *
 * Loaded as <script type="module"> from snippets/tk-vase-sheet.liquid, which
 * renders UNCONDITIONALLY in the initial page HTML inside the cart drawer —
 * so this executes on every page load (never via Section-Rendering injection,
 * which would not execute it). All handlers are DOCUMENT-DELEGATED and the
 * whole module is idempotent (__tkVaseBound), so it survives every cart morph
 * — the sheet itself sits outside the drawer's hydration key and is never
 * morphed anyway (see the placement comment in tk-vase-sheet.liquid).
 *
 * The sheet OPENS via Horizon's declarative dispatcher
 * (on:click="#tk-vase-sheetc/showDialog" on the per-line buttons in
 * cart-products.liquid). This file owns: option selection (including the
 * impossible-combination snap-back), per-variant price/image/availability,
 * TILE SELECTION, and the single commit that posts to the cart.
 *
 * SELECTION MODEL (Harry 2026-07-28 — parity with the PDP upsell drawer and
 * with UrbanStems). Tapping a card selects it: corner check, Add flips to
 * Remove, one card at a time. NOTHING is added yet. The add is committed by
 * the sticky "Add selected — $X" footer OR by closing the sheet with a card
 * still selected (✕, backdrop and Esc all count).
 *   Both routes funnel through ONE handler: the dialog's own `close` event.
 *   The footer button simply closes the sheet. `commit()` reads the pending
 *   selection, CLEARS IT FIRST, then fires the add — so it is idempotent and
 *   a footer-click-then-close cannot double-add.
 *   `close` does not bubble, so the listener is capture-phase on document
 *   (same trick cart-drawer.js uses for <details> toggle events); that keeps
 *   the module document-delegated and morph-proof.
 * The previous build added straight to the cart from the per-card button —
 * no way to change your mind, and it did not match the PDP.
 *
 * VARIANT MODEL — mirrors the proven tk-pdp.js upsell logic (F01/F02 fixes):
 *  - the hidden <select data-tk-vsel> is the single source of truth; each
 *    <option> carries data-opt1..3 / data-cents / data-money / data-img and a
 *    `disabled` flag for unavailable variants.
 *  - clicking an option value writes it into the card's data-sel<pos> and we
 *    look for an EXACT match across all positions. When the matrix is sparse
 *    (a Colour that only exists in one Finish) there is no exact match, so we
 *    SNAP to the first (preferably available) variant that honours the value
 *    the shopper just clicked and reflect that whole combination back into
 *    every pill group — the UI and the select can never disagree.
 *  - if a SELECTED card's combination turns out to be unavailable, the
 *    selection is dropped rather than left pointing at a variant that would
 *    422 on commit (the F01 lesson from the PDP drawer).
 *
 * Adds are PLAIN {id, quantity} lines — no properties, no pairing (that is
 * Phase 2 V2). The vase line renders via the drawer's normal section morph,
 * which also removes the per-line CTA buttons server-side (their Liquid gate
 * hides them once the cart holds as many vases as bouquets).
 */
import { cartService } from '@theme/tk-cart';

(function () {
  if (window.__tkVaseBound) return;
  window.__tkVaseBound = true;

  /** @type {Element | null} the one card currently chosen, if any */
  var selected = null;

  function sheet() {
    return document.getElementById('tk-vase-sheet');
  }

  function optVal(option, pos) {
    return option.dataset['opt' + pos] || '';
  }

  function wantedValues(card) {
    return [card.dataset.sel1 || '', card.dataset.sel2 || '', card.dataset.sel3 || ''];
  }

  /** exact match on all three positions — never join titles, a value may contain ' / ' */
  function findExact(sel, want) {
    for (var i = 0; i < sel.options.length; i++) {
      var o = sel.options[i];
      if (optVal(o, 1) === want[0] && optVal(o, 2) === want[1] && optVal(o, 3) === want[2]) return o;
    }
    return null;
  }

  /** sparse matrix: first (preferably available) variant honouring the clicked value */
  function findNearest(sel, pos, value) {
    var fallback = null;
    for (var i = 0; i < sel.options.length; i++) {
      var o = sel.options[i];
      if (optVal(o, pos) !== value) continue;
      if (!o.disabled) return o;
      if (!fallback) fallback = o;
    }
    return fallback;
  }

  /** write a variant's whole combination back into the card state + every pill group */
  function reflect(card, option) {
    for (var pos = 1; pos <= 3; pos++) {
      var value = optVal(option, pos);
      card.dataset['sel' + pos] = value;
      var group = card.querySelectorAll('[data-tk-vopt][data-pos="' + pos + '"]');
      for (var i = 0; i < group.length; i++) {
        var on = group[i].dataset.val === value;
        group[i].classList.toggle('tk-on', on);
        group[i].setAttribute('aria-checked', on ? 'true' : 'false');
        group[i].tabIndex = on ? 0 : -1;
      }
    }
  }

  /** resolve the card's current combination to an <option>, or null */
  function resolveVariant(card) {
    var sel = card.querySelector('[data-tk-vsel]');
    if (!sel) return null;
    var option = findExact(sel, wantedValues(card));
    if (option) sel.value = option.value;
    return option;
  }

  /** push the resolved variant into price, image, button state and the note line */
  function syncCard(card) {
    var option = resolveVariant(card);
    var add = card.querySelector('[data-tk-vadd]');
    var note = card.querySelector('[data-tk-vnote]');
    var price = card.querySelector('[data-tk-vprice]');
    var img = card.querySelector('[data-tk-vimg]');
    var usable = Boolean(option) && !option.disabled;

    if (option && price && option.dataset.money) price.textContent = option.dataset.money;
    if (option && option.dataset.img && img && img.getAttribute('src') !== option.dataset.img) {
      img.setAttribute('src', option.dataset.img);
    }

    if (add) add.disabled = !usable;
    if (note) {
      if (!option) note.textContent = 'That combination isn’t available.';
      else if (option.disabled) note.textContent = 'Sold out in this combination.';
      else note.textContent = '';
    }

    // never leave a selection pointing at something that would 422 on commit
    if (!usable && selected === card) clearSelection();
    else if (selected === card) paintFooter();

    return option;
  }

  // ---------- selection

  function paintCard(card) {
    var on = selected === card;
    card.classList.toggle('tk-vase__card--on', on);
    var add = card.querySelector('[data-tk-vadd]');
    var label = card.querySelector('[data-tk-vadd-label]');
    if (add) add.setAttribute('aria-pressed', on ? 'true' : 'false');
    if (label) label.textContent = on ? 'Remove' : 'Add';
  }

  function paintFooter() {
    var foot = document.querySelector('[data-tk-vfoot]');
    if (!foot) return;
    if (!selected) {
      foot.hidden = true;
      return;
    }
    var price = selected.querySelector('[data-tk-vprice]');
    var out = foot.querySelector('[data-tk-vfoot-price]');
    if (out && price) out.textContent = price.textContent;
    var btn = foot.querySelector('[data-tk-vconfirm]');
    if (btn) btn.setAttribute('aria-label', 'Add ' + (selected.dataset.title || 'vase') + ' to cart');
    foot.hidden = false;
  }

  function selectCard(card) {
    var option = resolveVariant(card);
    if (!option || option.disabled) return;
    var previous = selected;
    selected = card;
    if (previous && previous !== card) paintCard(previous);
    paintCard(card);
    paintFooter();
  }

  function clearSelection() {
    var previous = selected;
    selected = null;
    if (previous) paintCard(previous);
    paintFooter();
  }

  // ---------- options

  function pickOption(button) {
    var card = button.closest('[data-tk-vcard]');
    if (!card) return;
    var sel = card.querySelector('[data-tk-vsel]');
    if (!sel) return;
    var pos = Number(button.dataset.pos) || 1;
    var value = button.dataset.val || '';

    card.dataset['sel' + pos] = value;
    var group = card.querySelectorAll('[data-tk-vopt][data-pos="' + pos + '"]');
    for (var i = 0; i < group.length; i++) {
      var on = group[i] === button;
      group[i].classList.toggle('tk-on', on);
      group[i].setAttribute('aria-checked', on ? 'true' : 'false');
      group[i].tabIndex = on ? 0 : -1;
    }

    // no variant for this combination -> snap to the closest one that keeps the
    // value just clicked, and mirror it back into every group
    if (!findExact(sel, wantedValues(card))) {
      var nearest = findNearest(sel, pos, value);
      if (nearest) reflect(card, nearest);
    }

    syncCard(card);
    // picking a colour on an untouched tile means "this one" (PDP behaviour)
    if (selected !== card) selectCard(card);
  }

  // ---------- commit

  /**
   * The ONE place a vase reaches the cart. Clears the pending selection before
   * firing, so calling it twice (footer click then the close event) is a no-op
   * the second time.
   */
  function commit() {
    var card = selected;
    if (!card) return;
    var option = resolveVariant(card);
    clearSelection();
    if (!option || option.disabled) return;
    var vid = Number(option.value);
    if (!vid) return;
    var add = card.querySelector('[data-tk-vadd]');

    cartService
      .add([{ id: vid, quantity: 1 }], {
        source: 'tk-vase-sheet',
        sourceId: 'tk-vase-sheet',
        context: 'cart',
        autoOpen: false,
        dedupeKey: add || card,
        eventTarget: add || card,
      })
      .catch(function (err) {
        // the sheet is already closed by now — reopen it and say what happened
        // rather than failing silently. The selection stays cleared, so closing
        // again does not retry the same doomed add.
        var note = card.querySelector('[data-tk-vnote]');
        if (note) {
          if (err && err.requiresCartCheck) {
            note.textContent = 'We couldn’t confirm that — check your cart before retrying.';
          } else if (err && err.status === 422) {
            note.textContent = 'Sold out in this combination.';
          } else {
            note.textContent = 'That didn’t add — try again.';
          }
        }
        syncCard(card);
        var wrap = document.getElementById('tk-vase-sheetc');
        if (wrap && typeof wrap.showDialog === 'function' && !sheet()?.open) wrap.showDialog();
      });
  }

  function closeSheet() {
    var wrap = document.getElementById('tk-vase-sheetc');
    if (wrap && typeof wrap.closeDialog === 'function') {
      wrap.closeDialog();
      return;
    }
    var dlg = sheet();
    if (dlg && dlg.open) dlg.close();
  }

  // ---------- wiring (all document-delegated)

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    if (e.target.closest('[data-tk-vconfirm]')) {
      closeSheet(); // the close handler commits
      return;
    }

    var opt = e.target.closest('[data-tk-vopt]');
    if (opt) {
      pickOption(opt);
      return;
    }

    var card = e.target.closest('[data-tk-vcard]');
    if (!card) return;

    var add = e.target.closest('[data-tk-vadd]');
    if (add) {
      if (add.disabled) return;
      if (selected === card) clearSelection();
      else selectCard(card);
      return;
    }

    // the whole tile selects; links keep their own job
    if (e.target.closest('a, select')) return;
    if (selected === card) clearSelection();
    else selectCard(card);
  });

  // `close` does not bubble — capture phase on document still receives it
  document.addEventListener(
    'close',
    function (e) {
      if (e.target && e.target.id === 'tk-vase-sheet') commit();
    },
    true
  );

  // roving arrow keys inside an option group (matches the PDP upsell drawer)
  document.addEventListener('keydown', function (e) {
    if (!e.target || !e.target.closest) return;
    var button = e.target.closest('[data-tk-vopt]');
    if (!button) return;
    var group = button.closest('[data-tk-voptgroup]');
    if (!group) return;
    var opts = Array.prototype.slice.call(group.querySelectorAll('[data-tk-vopt]'));
    var i = opts.indexOf(button);
    var n = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = (i + 1) % opts.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = (i - 1 + opts.length) % opts.length;
    else if (e.key === 'Home') n = 0;
    else if (e.key === 'End') n = opts.length - 1;
    if (n === null) return;
    e.preventDefault();
    pickOption(opts[n]);
    opts[n].focus();
  });

  // one settling pass so price/availability match the pre-selected combination
  // even if Liquid's first-available variant and the lit pills ever diverge
  function initCards() {
    var cards = document.querySelectorAll('[data-tk-vcard]');
    for (var i = 0; i < cards.length; i++) syncCard(cards[i]);
    paintFooter();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCards, { once: true });
  } else {
    initCards();
  }
})();
